import React, { FC, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Flex, Text } from "@radix-ui/themes";
import { DateTime } from "luxon";
import { UuidFieldRow } from "@/components/table/uuid-field-row";

import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";

type CapturedAudioSummarySchema = {
  id: string;
  space_account_id: string;
  filename: string;
  mime_type: string;
  transcript_provider?: string | null;
  transcribed_at?: string | null;
  created_at: string;
  updated_at: string;
};

export const CapturedAudioRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const [searchTerm, setSearchTerm] = useState<string>(initialQuery);
  const [inputValue, setInputValue] = useState<string>(initialQuery);
  const [filterHasTranscript, setFilterHasTranscript] = useState<boolean | null>(null);
  const [filterHasFile, setFilterHasFile] = useState<boolean | null>(null);
  const [filterHasMp3, setFilterHasMp3] = useState<boolean | null>(null);
  const [filterProvider, setFilterProvider] = useState<string | null>(null);
  const [filterMimeType, setFilterMimeType] = useState<string>("");

  const isUuid = (val: string) =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
      val
    );

  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    setSearchTerm(query);
    setInputValue(query);
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      "captured-audio-search",
      searchTerm,
      filterHasTranscript,
      filterHasFile,
      filterHasMp3,
      filterProvider,
      filterMimeType,
    ],
    queryFn: async () => {
      const commonFilters: Record<string, unknown> = {
        limit: pageSize,
        filter_has_transcript: filterHasTranscript,
        filter_has_file: filterHasFile,
        filter_has_mp3: filterHasMp3,
        filter_by_provider: filterProvider ?? null,
        filter_by_mime_type: filterMimeType ? filterMimeType : null,
      };

      const term = searchTerm.trim();
      type SearchCapturedAudioResponse = { data?: { results: CapturedAudioSummarySchema[] } };
      const requests: Promise<SearchCapturedAudioResponse>[] = [];

      if (term && isUuid(term)) {
        requests.push(
          api.post("/internal/v1/captured-audio/search", {
            body: { ...commonFilters, filter_by_id: term },
          })
        );
        requests.push(
          api.post("/internal/v1/captured-audio/search", {
            body: { ...commonFilters, filter_by_space_account_id: term },
          })
        );
      } else {
        // Search using filters only
        requests.push(
          api.post("/internal/v1/captured-audio/search", {
            body: { ...commonFilters },
          })
        );
      }

      const responses = await Promise.all(requests);
      const all: CapturedAudioSummarySchema[] = responses.flatMap(r => r.data?.results ?? []);
      const uniqById: CapturedAudioSummarySchema[] = Array.from(
        new Map(all.map((row: CapturedAudioSummarySchema) => [row.id, row])).values()
      );
      // Order by created_at desc (most recent first)
      uniqById.sort(
        (a: CapturedAudioSummarySchema, b: CapturedAudioSummarySchema) =>
          new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf()
      );
      return { data: { results: uniqById, total: uniqById.length } };
    },
    enabled:
      searchTerm.trim().length > 0 ||
      filterHasTranscript !== null ||
      filterHasFile !== null ||
      filterHasMp3 !== null ||
      !!filterProvider ||
      !!filterMimeType,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleSearch = (query: string) => {
    if (query) {
      searchParams.set("query", query);
    } else {
      searchParams.delete("query");
    }
    setSearchParams(searchParams);
  };

  const handleView = (id: string) => {
    navigate(atlasRoutes.capturedAudioView({ params: { capturedAudioId: id } }).path);
  };

  const tableData = useMemo<CapturedAudioSummarySchema[]>(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <div>
          <h1>Captured Audio</h1>
          <DescriptionText>Search by CapturedAudio ID or Space Account ID.</DescriptionText>
        </div>
      </Flex>

      <SearchRow>
        <input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") handleSearch(inputValue);
          }}
          placeholder="Search by CapturedAudio ID or Space Account ID"
        />
        <Button onClick={() => handleSearch(inputValue)}>Search</Button>
      </SearchRow>

      <FiltersRow>
        <label>
          <input
            type="checkbox"
            checked={filterHasTranscript === true}
            onChange={e => setFilterHasTranscript(e.target.checked ? true : null)}
          />
          Has Transcript
        </label>
        <label>
          <input
            type="checkbox"
            checked={filterHasFile === true}
            onChange={e => setFilterHasFile(e.target.checked ? true : null)}
          />
          Has File
        </label>
        <label>
          <input
            type="checkbox"
            checked={filterHasMp3 === true}
            onChange={e => setFilterHasMp3(e.target.checked ? true : null)}
          />
          Has MP3
        </label>
        <select
          value={filterProvider ?? ""}
          onChange={e => setFilterProvider(e.target.value || null)}
        >
          <option value="">Any Provider</option>
          <option value="ASSEMBLY_AI">ASSEMBLY_AI</option>
          <option value="ASSEMBLY_AI_PRERECORDED">ASSEMBLY_AI_PRERECORDED</option>
          <option value="ASSEMBLY_AI_STREAMING">ASSEMBLY_AI_STREAMING</option>
          <option value="APPLE_SPEECH">APPLE_SPEECH</option>
        </select>
        <input
          value={filterMimeType}
          onChange={e => setFilterMimeType(e.target.value)}
          placeholder="Mime type (exact)"
        />
        <Button
          variant="soft"
          onClick={() => {
            setFilterHasTranscript(null);
            setFilterHasFile(null);
            setFilterHasMp3(null);
            setFilterProvider(null);
            setFilterMimeType("");
          }}
        >
          Clear Filters
        </Button>
      </FiltersRow>

      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching: {error.message}</div>}

      {tableData.length > 0 ? (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Created At</th>
                <th>ID</th>
                <th>Space Account</th>
                <th>Filename</th>
                <th>Mime</th>
                <th>Provider</th>
                <th>Transcribed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map(row => (
                <tr key={row.id}>
                  <td>
                    {row.created_at
                      ? DateTime.fromISO(row.created_at).toFormat("MMM d, yyyy h:mm a")
                      : "-"}
                  </td>
                  <td>
                    <UuidFieldRow id={row.id as unknown as string} />
                  </td>
                  <td>
                    <UuidFieldRow id={row.space_account_id as unknown as string} />
                  </td>
                  <td>{row.filename}</td>
                  <td>{row.mime_type}</td>
                  <td>{row.transcript_provider ?? "-"}</td>
                  <td>{row.transcribed_at ?? "-"}</td>
                  <td>
                    <Button variant="soft" onClick={() => handleView(row.id)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        !isLoading && searchTerm && <div>No records found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const SearchRow = styled(Flex)`
  gap: 8px;
  margin: 12px 0 16px 0;
`;

const FiltersRow = styled(Flex)`
  gap: 8px;
  margin: 0 0 12px 0;
  flex-wrap: wrap;
  align-items: center;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th,
  td {
    text-align: left;
    padding: 8px 6px;
    border-bottom: 1px solid var(--gray-5);
    white-space: nowrap;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;
